/**
 * OmegaCeoBriefingsPage — Phase 36.7a.
 *
 * List of past CEO daily briefings with client-side date filter.
 * Polls every 60 s.
 */

import { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiBaseUrl } from '@/api/http';
import { getAccessToken } from '@/api/supabase';

// ── Types ────────────────────────────────────────────────────────────────────

interface BriefingListItem {
  briefing_id: string;
  generated_at: number;
  narrative_length: number;
  status: string;
}

interface BriefingsListResponse {
  briefings: BriefingListItem[];
  total: number;
}

// ── Helpers (exported for tests) ─────────────────────────────────────────────

export function statusBadgeColor(status: string): string {
  if (status === 'complete') return '#34d399';
  if (status === 'pending')  return '#fbbf24';
  return 'var(--omega-ink-subtle)';
}

export function fmtBriefingDateShort(unixSecs: number): string {
  // Founder dashboard is English-only.
  return new Date(unixSecs * 1000).toLocaleString('en-US', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

// ── Data ─────────────────────────────────────────────────────────────────────

async function fetchBriefings(): Promise<BriefingsListResponse> {
  const token = await getAccessToken();
  const resp = await fetch(`${apiBaseUrl()}/internal/ceo/briefings?limit=50`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json() as Promise<BriefingsListResponse>;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
  borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
  fontSize: '8.5pt', padding: '4px 8px', outline: 'none',
};

// ── Page ─────────────────────────────────────────────────────────────────────

export function OmegaCeoBriefingsPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [dateFilter, setDateFilter] = useState('');

  const { data, isPending, isError } = useQuery({
    queryKey: ['ceo', 'briefings'],
    queryFn: fetchBriefings,
    refetchInterval: 60_000,
  });

  const briefings = data?.briefings ?? [];

  const filtered = dateFilter
    ? briefings.filter(b => {
        const d = new Date(b.generated_at * 1000);
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        return iso === dateFilter;
      })
    : briefings;

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA', to: '/omega' },
        { label: 'CEO', to: '/omega/ceo' },
        { label: 'Briefings' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, marginTop: 8, direction: 'ltr' }}>
        <h1 style={{ margin: 0, fontSize: '11pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Daily Briefings
        </h1>
        {data && (
          <span style={{
            background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 3, fontSize: '8pt', padding: '1px 7px', color: 'var(--omega-ink-subtle)',
          }}>
            {filtered.length}
          </span>
        )}
      </div>

      {/* Date filter */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center', direction: 'ltr' }}>
        <input
          type="text"
          placeholder="yyyy-mm-dd"
          value={dateFilter}
          onChange={e => setDateFilter(e.target.value)}
          style={inputStyle}
        />
      </div>

      {/* Column headers */}
      {!isPending && !isError && filtered.length > 0 && (
        <div style={{
          display: 'grid', gridTemplateColumns: '160px 100px 80px 80px',
          gap: 10, padding: '6px 12px',
          borderBottom: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
          marginBottom: 2, direction: 'ltr',
        }}>
          {['Date', 'Length', 'Status', ''].map(h => (
            <span key={h} style={{
              fontSize: '8.5pt', fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: 'var(--omega-ink-subtle)',
            }}>
              {h}
            </span>
          ))}
        </div>
      )}

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError   && <div style={{ color: '#f87171', fontSize: '9pt' }}>Failed to load briefings.</div>}

      {!isPending && !isError && filtered.length === 0 && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic', padding: '40px 0', textAlign: 'center', direction: 'ltr' }}>
          No briefings yet
        </div>
      )}

      {filtered.map(b => (
        <div
          key={b.briefing_id}
          style={{
            display: 'grid', gridTemplateColumns: '160px 100px 80px 80px',
            gap: 10, alignItems: 'center',
            padding: '10px 12px',
            borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
            direction: 'ltr',
          }}
        >
          <span style={{ fontSize: '9pt', color: 'var(--omega-ink)' }}>
            {fmtBriefingDateShort(b.generated_at)}
          </span>
          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
            {b.narrative_length} chars
          </span>
          <span style={{
            fontSize: '7.5pt', fontWeight: 700, padding: '1px 6px',
            border: `1px solid ${statusBadgeColor(b.status)}`,
            color: statusBadgeColor(b.status),
            borderRadius: 3, letterSpacing: '0.06em', textTransform: 'uppercase',
            justifySelf: 'start',
          }}>
            {b.status}
          </span>
          <Link
            to={`/omega/ceo/briefings/${b.briefing_id}`}
            style={{
              background: 'rgba(125,211,252,0.08)',
              border: '1px solid rgba(125,211,252,0.25)',
              borderRadius: 4, color: '#7dd3fc',
              fontFamily: 'var(--omega-mono)', fontSize: '8pt', fontWeight: 600,
              padding: '4px 10px', textDecoration: 'none', whiteSpace: 'nowrap',
            }}
          >
            Open →
          </Link>
        </div>
      ))}
    </div>
  );
}
